import { type AxiosResponse } from 'axios';
import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import CButton from '../../components/CButton';
import CLocalizedTextString from '../../components/CLocalizedTextString';
import CPageModule from '../../components/CPageModule';
import CPageModuleBrowsableTable from '../../components/CPageModuleBrowsableTable';
import { type TPandasDataFrameDict } from '../../components/TPandasDataFrameDict';
import Page from '../../components/layout/Page';
import { ETableIds } from '../../enums/ETableIds';
import { updateStateAttribute, updateStateResponseStatefulAttribute, useStateExtended } from '../../helpers/helper';
import { useAppSelector } from '../../hooks/hooks';
import type { IAugmentedBackendResponse } from '../../models/IAugmentedBackendResponse';
import type { IRequestParams } from '../../models/IRequestParams';
import type { IResponse } from '../../models/IResponse';
import type { ISortingMeta } from '../../models/ISortingMeta';
import { SvDownloadCenterServices } from '../../services/SvDownloadCenterServices';
import { SvLocalisationService } from '../../services/SvLocalisationService';
import CParameterizedDownloadButton from '../CParameterizedDownloadButton';

interface State {
  snapshot: IResponse<IAugmentedBackendResponse<TPandasDataFrameDict>> & IRequestParams;
}

export function PDownloadCenterIndex() {
  const title = CLocalizedTextString('downloadCenter.title');
  const { locale } = useAppSelector((s) => s.dashboard);
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultSorting: ISortingMeta = { sortCol: 0, ascending: 'ascending' };

  const [state, setState, getState] = useStateExtended<State>({
    snapshot: {
      state: { isLoading: true, isError: false },
      params: { sorting: defaultSorting }
    }
  });

  const fetchDownloads = async (page: number, sorting?: ISortingMeta, search?: string) => {
    await updateStateResponseStatefulAttribute<State, typeof state.snapshot.data>(
      { snapshot: (await getState()).snapshot },
      setState,
      async () =>
        await SvDownloadCenterServices.getDownloadLists({
          page,
          sorting: sorting ?? defaultSorting,
          search
        })
    );
    updateStateAttribute<State>(
      { snapshot: { ...(await getState()).snapshot, params: { sorting, search } } },
      setState
    );
  };

  const handleFileDownload = async (url: string, startInt?: number, endInt?: number) => {
    const isI18n = !startInt && !endInt && url.startsWith('api/i18n/file');

    let blob;
    let filename = 'download.csv';
    let headers;

    if (isI18n) {
      const query = url.split('?')[1] ?? '';
      const mode = new URLSearchParams(query).get('mode');
      let type: 'backend' | 'frontend' | 'homepage' = 'frontend';
      if (mode === 'backend') {
        type = 'backend';
      } else if (mode === 'homepage') {
        type = 'homepage';
      }
      const response = await SvLocalisationService.downloadI18nFile({ type });

      blob = response.getBlob();
      if (!blob) {
        throw new Error(await response.getMessage());
      }

      headers = (response.axiosResponseOrError as AxiosResponse).headers;
      filename = 'language.json';

      // Check content disposition for filename override
      const contentDisposition = headers?.['content-disposition'] as string | undefined;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch?.[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
    } else {
      const response = await SvDownloadCenterServices.downloadFile({
        url,
        startDate: startInt,
        endDate: endInt
      });

      blob = response.getBlob();
      if (!blob) {
        throw new Error(await response.getMessage());
      }

      headers = (response.axiosResponseOrError as AxiosResponse).headers;

      if (headers?.['content-disposition']) {
        const contentDisposition = headers['content-disposition'];
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch?.[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
    }

    const blobUrl = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();

    // Cleanup
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    }, 100);
  };

  useEffect(() => {
    const params = {
      sortCol: Number.parseInt(searchParams.get('sortCol') ?? defaultSorting.sortCol.toString()),
      ascending: (searchParams.get('ascending') as unknown as ISortingMeta['ascending']) ?? defaultSorting.ascending
    };
    const page = Number.parseInt(searchParams.get('page') ?? '1');
    const search = searchParams.get('search') ?? undefined;

    (async () => {
      await fetchDownloads(page, params, search);
    })();
  }, [locale]);

  const formatDownloadButton = (cell: string) => {
    let url;
    let hasAllConfig = false;
    let parsed: any = {};

    try {
      parsed = JSON.parse(String(cell ?? '{}'));
      const requiredKeys = ['url', 'start_date', 'end_date', 'min_date', 'max_date'];
      hasAllConfig = requiredKeys.every((key) => parsed[key] !== undefined);
      url = parsed?.url ?? '';
    } catch {
      url = String(cell ?? '');
    }
    const isI18nUrl = url.startsWith('api/i18n/file');

    return hasAllConfig ? (
      <CParameterizedDownloadButton
        url={url}
        start_date={parsed.start_date}
        end_date={parsed.end_date}
        min_date={parsed.min_date}
        max_date={parsed.max_date}
        onDownload={handleFileDownload}
      />
    ) : (
      <div className={'w-full flex items-center justify-center'}>
        {isI18nUrl ? <CButton.Download url={url} onDownload={handleFileDownload} /> : <CButton.Download url={url} />}
      </div>
    );
  };

  return (
    <Page title={title}>
      <CPageModule fullWidth>
        <CPageModule submodule>
          <h1>{title}</h1>
        </CPageModule>
        <CPageModuleBrowsableTable
          id={ETableIds.DOWNLOAD_CENTER}
          data={state.snapshot.data?.results}
          state={state.snapshot.state}
          tableMeta={state.snapshot.data?.meta}
          enableColumnToggle
          defaultColumnToggleArray={[true, false, true]}
          onUpdateTableMeta={async (nextMeta) => {
            const page = nextMeta.page ?? 1;
            const sorting = nextMeta.sorting ?? defaultSorting;
            const search = nextMeta.search ?? '';

            await fetchDownloads(page, sorting, search);

            searchParams.set('page', page.toString());
            searchParams.set('sortCol', sorting.sortCol.toString());
            searchParams.set('ascending', sorting.ascending);
            searchParams.set('search', search);
            setSearchParams(searchParams);
          }}
          columnConfig={[
            {
              span: 10,
              header: {
                style: () => ({ textAlign: 'left' })
              },
              body: {
                style: () => ({ textAlign: 'left' })
              }
            },
            {
              /* Type column */
            },
            {
              isSortable: false,
              body: {
                style: () => ({ textAlign: 'center' }),
                render: (context) => formatDownloadButton(context.value as string)
              }
            }
          ]}
          rowConfig={{}}
        />
      </CPageModule>
    </Page>
  );
}
